import React, { Dispatch, createRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
    Button,
    Col,
    FormGroup,
    Row,
    Heading,
    Input,
} from '@snsw/react-component-library';
import {
    ButtonsContainer,
    ErrorSummary,
    ActionType,
    BankDetails,
    scrollToTop,
    scrollToRef,
    ApplicationState,
    AppAction,
    StandardAppConfig,
    useApplicationContext
} from '../../index';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

const HeadingWrapper = styled.div`
    margin-top: 1.5rem;
`;

const InputWrapper = styled.div`
    margin-bottom: 1.5rem;
`;

const RequiredWrapper = styled.div`
    margin-top: 1.5rem;
`;

export const BankDetailsPage: React.FC<{
    globalState: ApplicationState, dispatch: Dispatch<AppAction>, appConfig?: StandardAppConfig
}> = props => {
    const history = useHistory();
    const { handleSubmit, control, formState: { errors } } = useForm<BankDetails>();
    const errorSummaryRef = createRef();
    const dispatch = props.dispatch;
    const { bankDetails } = props.globalState ;

    const errorSummaryRefRow = () => {
        scrollToRef(errorSummaryRef);
        createRef();
        scrollToFirstError();
    };

    const onSubmit = (d: any) => {
        scrollToTop();
        dispatch({ type: ActionType.UPDATE_BANK_DETAILS_ACTION, payload: { bankDetails: d } });
        history.push('/bank-review-page');
    };
    const submitForm = handleSubmit(onSubmit);

    const onBack = ( d: any ) => {
        scrollToTop();
        dispatch({ type: ActionType.UPDATE_BANK_DETAILS_ACTION, payload: { bankDetails: d } });
        history.push('/supporting-documents-page');
    };

    const scrollToFirstError = () => {
        if (Object.keys(errors).length === 1) {
            const errorsLength = errors ? Object.keys(errors).length : 0;
            const keys = errorsLength ? Object.keys(errors) : null;
            const errorVisible = document.querySelectorAll(`[name*="${keys}"],[id*="${keys}"]`);
            scrollToTop(errorVisible[0].getBoundingClientRect().top + window.scrollY);
        }
    };

    return (
        <>
            <Row>
                <Col span={ 6 }>
                    <RequiredWrapper>
                        All fields are required.
                    </RequiredWrapper>
                </Col>
            </Row>
            <Row ref={ errorSummaryRefRow }>
                <Col span={ 8 }>
                    {/*@ts-ignore*/}
                    <ErrorSummary
                        ref={ errorSummaryRef }
                        errors={ errors }
                        title='Your form has errors'
                        minErrors={ 2 }
                    />
                </Col>
            </Row>
            <Row>
                <Col span={ 6 }>
                    <HeadingWrapper>
                        <Heading level={ 3 }>Enter your bank details for payment</Heading>
                    </HeadingWrapper>
                </Col>
            </Row>

            <form onSubmit={ submitForm }>
                <Row>
                    <Col span={ 8 }>
                        <InputWrapper>
                            <Controller
                                name='accountName'
                                control={ control }
                                defaultValue={ bankDetails.accountName || '' }
                                render={ ({ field: { onChange, value }, fieldState: { error } }) => (
                                    <FormGroup
                                        id='accountName'
                                        label='Account name'
                                        helpMessage='This is usually the name of the account holder(s).'
                                        errorMessage={ error ? error.message : null }
                                        hasError={ Boolean(error) }
                                    >
                                        <Input
                                            name='accountName'
                                            value={ value }
                                            onChange={ onChange }
                                            maxLength={ 32 }
                                        />
                                    </FormGroup>
                                ) }
                                rules={ {
                                    required: 'Account name is required.',
                                    pattern: { value: /^[0-9A-Za-z'\s\-]{1,32}s*$/,
                                        message: 'Enter an account name with no special characters or symbols.'
                                    } } }
                            />
                        </InputWrapper>
                    </Col>
                </Row>
                <Row>
                    <Col span={ 3 }>
                        <InputWrapper>
                            <Controller
                                name='bsb'
                                control={ control }
                                defaultValue={ bankDetails.bsb || '' }
                                render={ ({ field: { onChange, value }, fieldState: { error } }) => (
                                    <FormGroup
                                        id='bsb'
                                        label='BSB'
                                        helpMessage='Enter your 6-digit BSB.'
                                        errorMessage={ error ? error.message : null }
                                        hasError={ Boolean(error) }
                                    >
                                        <Input
                                            name='bsb'
                                            value={ value }
                                            onChange={ onChange }
                                            maxLength={ 6 }
                                            margin={ { top: 'xxxl' } }
                                            style={ { maxWidth: '200px' } }
                                        />
                                    </FormGroup>
                                ) }
                                rules={ { required: 'BSB is required.', pattern: { value: /^s*-?[0-9]{6}s*$/, message: 'Enter a 6-digit BSB with no spaces or symbols.' } } }
                            />
                        </InputWrapper>
                    </Col>
                </Row>
                <Row>
                    <Col span={ 4 }>
                        <Controller
                            name='accountNumber'
                            control={ control }
                            defaultValue={ bankDetails.accountNumber || '' }
                            render={ ({ field: { onChange, value }, fieldState: { error } }) => (
                                <FormGroup
                                    id='accountNumber'
                                    label='Account number'
                                    helpMessage='Enter your 5 to 9 digit account number.'
                                    errorMessage={ error ? error.message : null }
                                    hasError={ Boolean(error) }
                                >
                                    <Input
                                        name='accountNumber'
                                        value={ value }
                                        onChange={ onChange }
                                        maxLength={ 9 }
                                    />
                                </FormGroup>
                            ) }
                            rules={ { required: 'Account number is required.', pattern: { value: /^s*-?[0-9]{5,9}s*$/, message: 'Enter a 5 to 9 digit account number with no spaces or symbols.' } } }
                        />
                    </Col>
                </Row>
                <ButtonsContainer>
                    <Button
                        className='button-item'
                        id='nextButton'
                        onClick={ submitForm }
                        data-testid='submitButton'
                    >
                        Confirm bank details
                    </Button>
                </ButtonsContainer>
                <Button
                    id='backButton'
                    onClick={ onBack }
                    theme='back'
                >
                    Back
                </Button>
            </form>
        </>
    );
};

export const GaasBankDetailsPage: React.FC = () => {
    const { globalState, dispatch } = useApplicationContext();
    return <div className='wrapper'>
        <BankDetailsPage
            globalState={ globalState }
            dispatch={ dispatch }
        />
    </div>;
};
